import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconSearch } from '@/components/ui/icons';
import { TeeInput } from '@/components/ui/tee-input';
import { TeeDropDownList } from '@/components/ui/tee-dropdown-list';
import { TeeButton } from '@/components/ui/tee-button';
import { TeeLoader } from '@/components/ui/tee-loader';
import { contentItemService } from '@/services/content-item.service';
import { useTaxonomy } from '@/hooks/useTaxonomy';
import { TaxonomyType } from '@/types/taxonomy';
import { ContentItem, ContentMediaType, ContentStatus } from '@/types/contentItem';
import ContentItemPreviewModal from '@/pages/content-library/ContentItemPreviewModal';
import { ResourceFinderCard } from './ResourceFinderCard';
import { MEDIA_TYPE_LABEL_KEY } from '@/utils/contentMediaType';

// Default column layout for the standalone Resource Finder page, where the
// grid renders directly in the viewport. Tailwind breakpoints here are
// viewport-relative, not container-relative — callers rendering the grid
// inside a width-constrained container (e.g. TeeFormModal) must pass a
// narrower gridClassName or the columns will overflow the container.
const DEFAULT_GRID_COLUMNS = 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';

export interface ContentCardGridProps {
  onAttach: (item: ContentItem) => void;
  excludeIds?: string[];
  enablePreview?: boolean;
  attaching?: boolean;
  className?: string;
  // Column classes for the results grid. Defaults to DEFAULT_GRID_COLUMNS
  // (the standalone Resource Finder page's 1/2/3/4-column layout). Callers
  // rendering inside a width-constrained container must override this since
  // Tailwind breakpoints are viewport-relative, not container-relative.
  gridClassName?: string;
}

export function ContentCardGrid({
  onAttach,
  excludeIds = [],
  enablePreview = true,
  attaching = false,
  className,
  gridClassName = DEFAULT_GRID_COLUMNS,
}: ContentCardGridProps) {
  const { t } = useTranslation();
  const { options: taxonomyOptions } = useTaxonomy();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [mediaType, setMediaType] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [archetypeId, setArchetypeId] = useState('');

  const [results, setResults] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [previewItem, setPreviewItem] = useState<ContentItem | null>(null);

  // Debounce the search term only — filter dropdown changes fire immediately
  // via the effect below since they are already discrete, low-frequency events.
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [search]);

  const loadResults = async () => {
    setLoading(true);
    setError(false);
    try {
      const result = await contentItemService.listContentItems({
        status: ContentStatus.PUBLISHED,
        mediaType: mediaType ? (mediaType as ContentMediaType) : undefined,
        categoryId: categoryId || undefined,
        archetypeId: archetypeId || undefined,
        search: debouncedSearch || undefined,
        limit: 24,
        offset: 0,
      });
      setResults(result.data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, mediaType, categoryId, archetypeId]);

  const categoryLabelById = useMemo(
    () => Object.fromEntries(taxonomyOptions[TaxonomyType.CATEGORY].map((o) => [o.value, o.label])),
    [taxonomyOptions]
  );
  const archetypeLabelById = useMemo(
    () => Object.fromEntries(taxonomyOptions[TaxonomyType.ARCHETYPE].map((o) => [o.value, o.label])),
    [taxonomyOptions]
  );

  const mediaTypeOptions = [
    { label: t('contentLibrary.filterAll'), value: '' },
    ...Object.values(ContentMediaType).map((value) => ({
      label: t(MEDIA_TYPE_LABEL_KEY[value]),
      value,
    })),
  ];

  const categoryOptions = [
    { label: t('contentLibrary.filterAll'), value: '' },
    ...taxonomyOptions[TaxonomyType.CATEGORY],
  ];

  const archetypeOptions = [
    { label: t('contentLibrary.filterAll'), value: '' },
    ...taxonomyOptions[TaxonomyType.ARCHETYPE],
  ];

  return (
    <div className={`flex flex-col gap-4${className ? ` ${className}` : ''}`}>
      <div className="flex flex-col gap-3">
        <TeeInput
          type="text"
          placeholder={t('contentLibrary.searchPlaceholder')}
          leadingIcon={<IconSearch className="w-4 h-4" />}
          inputSize="s"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="flex gap-3">
          <TeeDropDownList
            options={mediaTypeOptions}
            value={mediaType}
            onChange={(value) => setMediaType(String(value))}
            placeholder={t('contentLibrary.filterMediaType')}
            className="flex-1"
          />
          <TeeDropDownList
            options={categoryOptions}
            value={categoryId}
            onChange={(value) => setCategoryId(String(value))}
            placeholder={t('contentLibrary.filterCategory')}
            className="flex-1"
          />
          <TeeDropDownList
            options={archetypeOptions}
            value={archetypeId}
            onChange={(value) => setArchetypeId(String(value))}
            placeholder={t('contentLibrary.filterArchetype')}
            className="flex-1"
          />
        </div>
      </div>

      {loading ? (
        <TeeLoader size="md" />
      ) : error ? (
        <div className="flex flex-col items-center gap-3 py-8">
          <p className="text-body-s text-[var(--text-content-sub-title)]">
            {t('contentLibrary.errorLoadItems')}
          </p>
          <TeeButton
            variant="secondary"
            size="s"
            text={t('contentLibrary.retry')}
            onClick={loadResults}
          />
        </div>
      ) : results.length === 0 ? (
        <div className="flex flex-col items-center gap-1 py-8 text-center">
          <p className="text-body-s font-semibold text-[var(--text-content-title)]">
            {t('contentLibrary.emptyFinderHeading')}
          </p>
          <p className="text-body-s text-[var(--text-content-sub-title)]">
            {t('contentLibrary.emptyFinderBody')}
          </p>
        </div>
      ) : (
        <div className={`grid ${gridClassName} gap-4`}>
          {results.map((item) => {
            const categoryLabels = item.categoryIds
              .map((id) => categoryLabelById[id])
              .filter((label): label is string => Boolean(label));
            const archetypeLabels = item.archetypeIds
              .map((id) => archetypeLabelById[id])
              .filter((label): label is string => Boolean(label));
            const isExcluded = excludeIds.includes(item.id);

            return (
              <ResourceFinderCard
                key={item.id}
                item={item}
                categoryLabels={categoryLabels}
                archetypeLabels={archetypeLabels}
                attachLabel={
                  isExcluded ? t('trainingProgram.alreadyAdded') : t('contentLibrary.attach')
                }
                attachDisabled={isExcluded}
                disabled={attaching}
                onAttach={onAttach}
                onPreview={enablePreview ? setPreviewItem : undefined}
              />
            );
          })}
        </div>
      )}

      {enablePreview && (
        <ContentItemPreviewModal
          isOpen={previewItem !== null}
          item={previewItem}
          onClose={() => setPreviewItem(null)}
        />
      )}
    </div>
  );
}
